import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  CalendarToday as CalendarIcon,
  Visibility as VisibilityIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  AttachMoney as MoneyIcon,
  Group as GroupIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { API_BASE } from '../config';

interface IncomeProjectionItem {
  email: string;
  name: string;
  planType: 'normal' | 'platinum';
  incomeType: 'self' | 'team';
  projectionDate: string;
  projectedAmount: number;
  currency: string;
  memberEmail?: string;
  level?: number;
}

interface PaginatedResponse {
  data: IncomeProjectionItem[];
  total: number;
  page: number;
  totalPages: number;
  summary: {
    totalProjectedAmount: number;
    selfIncomeTotal: number;
    teamIncomeTotal: number;
    normalPlanTotal: number;
    platinumPlanTotal: number;
    userCount: number;
  };
  period: {
    type: string;
    startDate: string;
    endDate: string;
  };
}

interface RealTimeStats {
  normalIncome: number;
  platinumIncome: number;
  teamIncome: number;
  platinumTeamIncome: number;
  totalIncome: number;
  totalTransactions: number;
  timestamp: string;
  normalIncomeCount: number;
  platinumIncomeCount: number;
}

const IncomeStatistics: React.FC = () => {
  // State management
  const [period, setPeriod] = useState<'weekly' | 'monthly' | 'yearly' | 'custom'>('weekly');
  const [planType, setPlanType] = useState<'all' | 'normal' | 'platinum'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [data, setData] = useState<PaginatedResponse | null>(null);
  const [realtimeStats, setRealtimeStats] = useState<RealTimeStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval] = useState(30);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [selectedChart, setSelectedChart] = useState<'daily' | 'distribution' | 'trend'>('daily');
  const [timeLeft, setTimeLeft] = useState(refreshInterval);

  // Fetch income projections
  const fetchProjections = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        period,
        planType,
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        sortBy: 'projectedAmount',
        sortOrder: 'desc'
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (period === 'custom' && startDate && endDate) {
        params.append('startDate', startDate.toISOString());
        params.append('endDate', endDate.toISOString());
      }

      const response = await axios.get(
        `${API_BASE}/api/incomestats/projections?${params}`
      );

      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch data');
      }
    } catch (error: any) {
      console.error('Error fetching projections:', error);
      setError(error.response?.data?.message || 'Network error occurred');
    } finally {
      setLoading(false);
    }
  }, [period, planType, searchTerm, startDate, endDate, page, rowsPerPage]);

  // Fetch real-time stats
  const fetchRealtimeStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE}/api/incomestats/realtime`
      );

      if (response.data.success) {
        setRealtimeStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching real-time stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchProjections();
    fetchRealtimeStats();
  }, [fetchProjections, fetchRealtimeStats]);

  // Auto-refresh setup with countdown
  useEffect(() => {
    if (!autoRefresh) {
      setTimeLeft(refreshInterval);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          fetchProjections();
          fetchRealtimeStats();
          return refreshInterval;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [autoRefresh, refreshInterval, fetchProjections, fetchRealtimeStats]);

  const handleManualRefresh = () => {
    fetchProjections();
    fetchRealtimeStats();
    setTimeLeft(refreshInterval);
  };

  useEffect(() => {
    setTimeLeft(refreshInterval);
  }, [period, planType, searchTerm, startDate, endDate, page, rowsPerPage, refreshInterval]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const calculateDateRange = () => {
    const now = new Date();
    switch (period) {
      case 'weekly':
        return {
          start: format(startOfWeek(now), 'MMM dd'),
          end: format(endOfWeek(now), 'MMM dd, yyyy')
        };
      case 'monthly':
        return {
          start: format(startOfMonth(now), 'MMM dd'),
          end: format(endOfMonth(now), 'MMM dd, yyyy')
        };
      case 'yearly':
        return {
          start: format(startOfYear(now), 'MMM dd, yyyy'),
          end: format(endOfYear(now), 'MMM dd, yyyy')
        };
      default:
        return {
          start: startDate ? format(startDate, 'MMM dd, yyyy') : '-',
          end: endDate ? format(endDate, 'MMM dd, yyyy') : '-'
        };
    }
  };

  const prepareChartData = () => {
    if (!data) return [];

    const dailyTotals: { [key: string]: number } = {};
    data.data.forEach(item => {
      const date = format(new Date(item.projectionDate), 'MMM dd');
      dailyTotals[date] = (dailyTotals[date] || 0) + item.projectedAmount;
    });

    return Object.entries(dailyTotals)
      .map(([date, amount]) => ({
        date,
        amount: parseFloat(amount.toFixed(2))
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  const planDistribution = [
    { name: 'Normal Plan', value: data?.summary.normalPlanTotal || 0, color: '#1976d2' },
    { name: 'Platinum Plan', value: data?.summary.platinumPlanTotal || 0, color: '#9c27b0' }
  ];

  const exportToCSV = () => {
    if (!data) return;

    const headers = ['Email', 'Name', 'Plan Type', 'Income Type', 'Projection Date', 'Amount', 'Member', 'Level'];
    const rows = data.data.map(item => [
      item.email,
      item.name,
      item.planType,
      item.incomeType,
      item.projectionDate,
      item.projectedAmount.toFixed(2),
      item.memberEmail || '-',
      item.level || '-'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `income-projections-${period}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const dateRange = calculateDateRange();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 2, md: 3 }, pb: 10 }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 4,
          gap: 2
        }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
              Income Projections
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Real-time monitoring of investment income
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'space-between', md: 'flex-end' } }}>
            <Tooltip title="Refresh data">
              <IconButton
                onClick={handleManualRefresh}
                color="primary"
                size="large"
                className={loading || statsLoading ? 'animate-spin' : ''}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportToCSV}
              sx={{ flexGrow: { xs: 1, md: 0 }, borderRadius: 3 }}
            >
              Export
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Quick Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden', borderRadius: 4 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <MoneyIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Total Projected
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                  {formatCurrency(data?.summary.totalProjectedAmount || 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {dateRange.start} - {dateRange.end}
                </Typography>
              </CardContent>
              {loading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />}
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', borderRadius: 4 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <GroupIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Active Users
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                  {data?.summary.userCount || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Across all plan types
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', borderRadius: 4 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Self Income
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1, color: 'success.main', fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                  {formatCurrency(data?.summary.selfIncomeTotal || 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Direct investment returns
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', borderRadius: 4 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUpIcon color="warning" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Team Income
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1, color: 'warning.main', fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                  {formatCurrency(data?.summary.teamIncomeTotal || 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Network referral returns
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Real-time Statistics Section */}
        <Card sx={{ mb: 4, borderRadius: 4 }}>
          <CardHeader
            title="Today's Real-time Performance"
            action={
              <Chip
                label={`Last sync: ${realtimeStats ? format(new Date(realtimeStats.timestamp), 'HH:mm:ss') : '...'}`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            }
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 3, textAlign: 'center' }}>
                  <Typography variant="caption" color="primary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Normal Self Income</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatCurrency(realtimeStats?.normalIncome || 0)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, bgcolor: 'secondary.50', borderRadius: 3, textAlign: 'center' }}>
                  <Typography variant="caption" color="secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Platinum Se</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatCurrency(realtimeStats?.platinumIncome || 0)}</Typography>
                </Paper>
              </Grid>
              {/* <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 3, textAlign: 'center' }}>
                  <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Team</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatCurrency((realtimeStats?.teamIncome || 0) + (realtimeStats?.platinumTeamIncome || 0))}</Typography>
                </Paper>
              </Grid> */}
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, bgcolor: 'success.50', borderRadius: 3, textAlign: 'center' }}>
                  <Typography variant="caption" color="success.dark" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Total Today</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>{formatCurrency(realtimeStats?.totalIncome || 0)}</Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Filters and Controls */}
        <Card sx={{ mb: 4, borderRadius: 4 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Period</InputLabel>
                  <Select value={period} label="Period" onChange={(e) => setPeriod(e.target.value as any)}>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                    <MenuItem value="custom">Custom Range</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Plan Type</InputLabel>
                  <Select value={planType} label="Plan Type" onChange={(e) => setPlanType(e.target.value as any)}>
                    <MenuItem value="all">All Plans</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="platinum">Platinum</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {period === 'custom' && (
                <>
                  <Grid item xs={12} sm={6} md={2}>
                    <DatePicker label="Start" value={startDate} onChange={setStartDate} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <DatePicker label="End" value={endDate} onChange={setEndDate} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                  </Grid>
                </>
              )}

              <Grid item xs={12} sm={6} md={period === 'custom' ? 2 : 5}>
                <TextField
                  fullWidth size="small" placeholder="Search..."
                  value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && fetchProjections()}
                  InputProps={{ startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} /> }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between', gap: 2
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Chip
                      icon={<TimeIcon />}
                      label={`Auto-refresh: ${autoRefresh ? 'ON' : 'OFF'}`}
                      color={autoRefresh ? 'success' : 'default'}
                      onClick={() => setAutoRefresh(!autoRefresh)}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                    {autoRefresh && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Next refresh in {timeLeft}s
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant={viewMode === 'table' ? 'contained' : 'outlined'}
                      size="small" onClick={() => setViewMode('table')}
                      sx={{ borderRadius: 2 }}
                    >
                      Table
                    </Button>
                    <Button
                      variant={viewMode === 'grid' ? 'contained' : 'outlined'}
                      size="small" onClick={() => setViewMode('grid')}
                      sx={{ borderRadius: 2 }}
                    >
                      Cards
                    </Button>
                    <IconButton onClick={() => setFilterDialogOpen(true)} color="primary">
                      <FilterIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Charts Section */}
        <Card sx={{ mb: 4, borderRadius: 4 }}>
          <CardHeader
            title="Visual Analytics"
            sx={{
              flexDirection: { xs: 'column', sm: 'row' },
              '& .MuiCardHeader-action': { m: 0, mt: { xs: 2, sm: 0 }, alignSelf: { xs: 'stretch', sm: 'center' } }
            }}
            action={
              <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: { xs: 1, sm: 0 } }}>
                <Button variant={selectedChart === 'daily' ? 'contained' : 'outlined'} size="small" onClick={() => setSelectedChart('daily')} sx={{ whiteSpace: 'nowrap', borderRadius: 2 }}>Daily</Button>
                <Button variant={selectedChart === 'distribution' ? 'contained' : 'outlined'} size="small" onClick={() => setSelectedChart('distribution')} sx={{ whiteSpace: 'nowrap', borderRadius: 2 }}>Dist</Button>
                <Button variant={selectedChart === 'trend' ? 'contained' : 'outlined'} size="small" onClick={() => setSelectedChart('trend')} sx={{ whiteSpace: 'nowrap', borderRadius: 2 }}>Trend</Button>
              </Box>
            }
          />
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            <Box sx={{ height: { xs: 300, sm: 400 } }}>
              <ResponsiveContainer width="100%" height="100%">
                {selectedChart === 'daily' ? (
                  <BarChart data={prepareChartData()}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} />
                    <YAxis axisLine={false} tickLine={false} />
                    <RechartsTooltip />
                    <Bar dataKey="amount" fill="#1976d2" radius={[4, 4, 0, 0]} />
                  </BarChart>
                ) : selectedChart === 'distribution' ? (
                  <PieChart>
                    <Pie data={planDistribution} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {planDistribution.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                ) : (
                  <AreaChart data={prepareChartData()}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} />
                    <YAxis axisLine={false} tickLine={false} />
                    <RechartsTooltip />
                    <Area type="monotone" dataKey="amount" stroke="#1976d2" fill="#1976d2" fillOpacity={0.1} />
                  </AreaChart>
                )}
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        {/* Data Table / Grid */}
        <Card sx={{ borderRadius: 4 }}>
          <CardHeader title="Projection Details" subheader={`Records: ${data?.total || 0}`} />
          <CardContent sx={{ p: { xs: 0, sm: 2 } }}>
            {viewMode === 'table' ? (
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.data.map((item, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{item.email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={item.planType} size="small" color={item.planType === 'platinum' ? 'secondary' : 'primary'} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>{item.incomeType}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(item.projectedAmount)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Grid container spacing={2} sx={{ p: 2 }}>
                {data?.data.map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.name}</Typography>
                        <Chip label={item.planType} size="small" />
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">{item.email}</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, p: 0.5, bgcolor: 'gray.100', borderRadius: 1 }}>{item.incomeType}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main' }}>{formatCurrency(item.projectedAmount)}</Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]} component="div"
              count={data?.total || 0} rowsPerPage={rowsPerPage} page={page}
              onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardContent>
        </Card>

        {/* Filter Dialog */}
        <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle sx={{ fontWeight: 700 }}>Advanced Filters</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Income Type</InputLabel>
                <Select label="Income Type" defaultValue="all">
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="self">Self</MenuItem>
                  <MenuItem value="team">Team</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Min" type="number" fullWidth size="small" />
                <TextField label="Max" type="number" fullWidth size="small" />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setFilterDialogOpen(false)} sx={{ fontWeight: 700 }}>Cancel</Button>
            <Button variant="contained" onClick={() => setFilterDialogOpen(false)} sx={{ fontWeight: 700, borderRadius: 2 }}>Apply</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default IncomeStatistics;